import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { isSameDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import API from 'api';
import { useAddAllTurnaroundsToDelivery } from 'api/hooks/useAddAllTurnaroundsToDelivery';
import { useCreateDelivery } from 'api/hooks/useCreateDelivery';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import { useDeliveryReceiveDispatch } from 'api/hooks/useDeliveryReceiveDispatch';
import { useOrderMutation } from 'api/hooks/useOrderMutation';
import { useUpdateDelivery } from 'api/hooks/useUpdateDelivery';
import { DeliveryBody, DeliveryId, UpdatedDelivery } from 'api/models/Deliveries';
import { Implant } from 'api/models/Implants';
import { OrderSummary } from 'api/models/Orders';
import { KitInstanceId } from 'api/models/Sets';
import Dialog, { DialogProps } from 'components/Dialog';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import { useDeliveryContentsOptions } from 'hooks/useDeliveryContentsOptions';
import useSupplierRepSelectOptions from 'hooks/useSupplierRepSelectOptions';
import useSupplierSelectOptions from 'hooks/useSupplierSelectOptions';
import useSurgeonSelectOptions from 'hooks/useSurgeonSelectOptions';
import { replaceTimeInfoWithUtcInDate } from 'utils/helpers/DateTimeFormat.helpers';
import DeliverySave from './components/DeliverySave';
import DeliverySelect from './components/DeliverySelect';
import OrdersSearch from './components/OrdersSearch';
import ReceiveDeliverySuccessDialog from './components/ReceiveDeliverySuccessDialog';
import SurgerySelect from './components/SurgerySelect';
import { INITIAL_RECEIVE_DELIVERY_FIELDS, useReceiveDeliveryFields } from './hooks/useReceiveDeliveryFields';

type Props = Omit<DialogProps, 'open' | 'onCancel' | 'onConfirm'> & {
  deliveryId?: number;
  supplierId?: string; // if provided, set the default supplier and disable changing supplier
  surgeonId?: string; // if provided, set the default surgeon
  surgeryDate?: Date; // if provided, set the default surgery date
  turnaroundSets?: KitInstanceId[];
  turnaroundImplants?: Implant['implantInstanceId'][];
  turnaroundOriginDeliveryId?: string;
  onComplete?: (deliveryId?: DeliveryId, deliveryNumber?: string) => void;
};

const dialogWidthSx = { '& .MuiPaper-root': { maxWidth: 'unset', width: 'auto' } };

export const ReceiveDeliveryDialog = ({
  deliveryId: _deliveryId,
  supplierId,
  surgeonId,
  surgeryDate,
  turnaroundSets,
  turnaroundImplants,
  turnaroundOriginDeliveryId,
  onClose,
  onComplete,
  ...props
}: Props) => {
  const lang = useLanguage()[0].ReceiveDeliveryDialog;

  const [step, setStep] = useState<'search' | 'deliveries' | 'surgeries' | 'save' | 'done'>('search');
  const [deliveryId, setDeliveryId] = useState(_deliveryId);
  const [historyStack, setHistoryStack] = useState<VoidFunction[]>([]);
  const [receiveSuccessDeliveryId, setReceiveSuccessDeliveryId] = useState<number | null>(null);
  // show a warning dialog if the form is filled but not saved
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  // first screen searches for matching orders and sets them here
  const [matchingOrders, setMatchingOrders] = useState<OrderSummary[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [haveDefaultSupplier, setHaveDefaultSupplier] = useState(false);

  const { fields, setField, setFields, resetFields } = useReceiveDeliveryFields();

  const [orderRecordId, setOrderRecordId] = useState<string | undefined>();

  const { data: delivery, isLoading: deliveryLoading, isFetched: deliveryFetched } = useDelivery(deliveryId);
  const isDispatchedDelivery = delivery?.status == 'OUTBOUND' || delivery?.status == 'DISPATCHED';

  const { data: user, isLoading: userLoading } = useCurrentUser();

  const { surgeonOptions, surgeonsLoading } = useSurgeonSelectOptions();
  const { suppliers, suppliersLoading, supplierOptions } = useSupplierSelectOptions();

  const supplierIdForLookups = fields.supplier?.id || delivery?.supplier.tenantId;
  const selectedSupplier = useMemo(
    () => suppliers?.find(s => s.tenantId == supplierIdForLookups),
    [supplierIdForLookups, suppliers],
  );
  const isActiveSupplier = selectedSupplier?.activeConnectUser;

  const { supplierRepOptions, isLoading: supplierRepOptionsLoading } = useSupplierRepSelectOptions({
    supplierId: supplierIdForLookups,
  });

  const deliveryContentsOptions = useDeliveryContentsOptions();

  const { mutateAsync: fetchOrder } = useOrderMutation();

  const { mutateAsync: createDelivery, isLoading: createDeliveryLoading } = useCreateDelivery();
  const { mutateAsync: deliveryReceiveDispatch, isLoading: deliveryReceiveDispatchLoading } =
    useDeliveryReceiveDispatch();
  const { mutateAsync: updateDelivery, isLoading: updateDeliveryLoading } = useUpdateDelivery();
  const { mutateAsync: addAllTurnaroundsToDelivery, isLoading: addAllTurnaroundsToDeliveryLoading } =
    useAddAllTurnaroundsToDelivery();

  // one or more of the shipper details values is required
  const hasShipperDetails =
    fields.numRoadCases || fields.numTubs || fields.numBoxes || fields.numPelicanCases || fields.numDoubleRoadCases;

  const isLoadingDelivery = !deliveryFetched && deliveryLoading;
  const isLoading =
    isLoadingDelivery || suppliersLoading || surgeonsLoading || supplierRepOptionsLoading || userLoading;
  const isLoadingInitial = !initialLoadDone && isLoading;
  const isSaving =
    createDeliveryLoading ||
    deliveryReceiveDispatchLoading ||
    updateDeliveryLoading ||
    addAllTurnaroundsToDeliveryLoading;

  useEffect(() => {
    if (initialLoadDone || isLoading) return;

    setInitialLoadDone(true);
  }, [initialLoadDone, isLoading]);

  // if we have a delivery, set the values
  useEffect(
    () => {
      if (isLoadingInitial) return;

      // if no delivery, init the receivedDate to today
      if (!delivery) {
        if (!fields.receivedDate) {
          setField('receivedDate', new Date());
        }
        return;
      }

      // populate the fields with the delivery data
      setFields({
        ...fields,
        receivedDate: new Date(delivery.receivedDetails?.receivedAt || Date.now()),
        supplier: supplierOptions.find(supplier => supplier.id == delivery.supplier.tenantId),
        supplierRep: delivery.supplier.deviceRepId
          ? supplierRepOptions.find(rep => rep.id == delivery.supplier.deviceRepId)
          : undefined,
        surgeon: surgeonOptions.find(surgeon => surgeon.id == delivery.surgery.surgeonId),
        surgeryDate: new Date(delivery.surgery.surgeryDate),
        orderNumber: delivery.orderNumber,
        deliveryNumber: delivery.deliveryNumber,
        surgeryReference: delivery.surgery.surgeryReference,
        patientInitials: delivery.patientInitials || fields.patientInitials || '',
        // ignore 0 "No content type specified" to force selection
        deliveryContents: delivery.content
          ? deliveryContentsOptions.find(content => content.id == delivery.content)
          : undefined,
        shipper: delivery.shipper,
        shipmentId: delivery.shipmentId,
        numRoadCases: delivery.shipperDetails?.numRoadCases || 0,
        numTubs: delivery.shipperDetails?.numTubs || 0,
        numBoxes: delivery.shipperDetails?.numBoxes || 0,
        numPelicanCases: delivery.shipperDetails?.numPelicanCases || 0,
        numDoubleRoadCases: delivery.shipperDetails?.numDoubleRoadCases || 0,
      });

      // Only auto navigate to the save step if the patient initials are already set
      // Otherwise, allow the user to enter the patient initials
      if (delivery.patientInitials || fields.patientInitials) {
        // navigate to the save step
        setStep('save');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want this to run after initial load and when a delivery is loaded
    [delivery?.deliveryNumber, isLoadingInitial],
  );

  // set the default supplier if it's provided
  useEffect(() => {
    if (!supplierId) return;

    if (haveDefaultSupplier) return;

    const defaultSupplier = supplierOptions.find(supplier => supplier.id === supplierId);
    if (!defaultSupplier) return;

    setField('supplier', defaultSupplier);
    setHaveDefaultSupplier(true);
  }, [setField, supplierId, supplierOptions, haveDefaultSupplier]);

  // If the surgeonId is provided, set the default surgeon
  useEffect(() => {
    if (!surgeonId || !surgeonOptions) return;

    const defaultSurgeon = surgeonOptions.find(surgeon => surgeon.id === surgeonId);
    if (!defaultSurgeon) return;

    setField('surgeon', defaultSurgeon);
  }, [setField, surgeonId, surgeonOptions]);

  // If the surgeryDate is provided, set the default surgery date
  useEffect(() => {
    if (!surgeryDate) return;

    setField('surgeryDate', surgeryDate);
  }, [setField, surgeryDate]);

  const onOrdersSearchContinue = useCallback(
    (orders: OrderSummary[]) => {
      setMatchingOrders(orders);
      const foundDispatchedDeliveries = orders.some(o => o.deliveries.some(d => d.deliveryStage == 'OUTBOUND'));
      const foundSurgeriesWithSurgeryReference = orders.some(o => o.surgeryReference || !o.supplierCreated);
      const nextStep = delivery
        ? 'save'
        : isActiveSupplier || foundDispatchedDeliveries
        ? 'deliveries'
        : foundSurgeriesWithSurgeryReference
        ? 'surgeries'
        : 'save';
      setStep(nextStep);

      setHistoryStack([
        ...historyStack,
        () => {
          setMatchingOrders([]);
          setStep('search');
        },
      ]);
    },
    [delivery, historyStack, isActiveSupplier],
  );

  const onDeliverySelectContinue = useCallback(
    (deliveryId?: number) => {
      if (deliveryId) {
        // simply setting the delivery number will cause the delivery
        //  to be loaded and the save step to be shown
        setDeliveryId(deliveryId);
      } else {
        const foundSurgeriesWithSurgeryReference = matchingOrders.some(o => o.surgeryReference || !o.supplierCreated);
        const nextStep = foundSurgeriesWithSurgeryReference ? 'surgeries' : 'save';
        setStep(nextStep);
      }

      setHistoryStack([
        ...historyStack,
        () => {
          setDeliveryId(undefined); // defined as undefined !! smort ts
          setFields({
            ...INITIAL_RECEIVE_DELIVERY_FIELDS,
            receivedDate: fields.receivedDate,
            supplier: fields.supplier,
            surgeon: fields.surgeon,
            surgeryDate: fields.surgeryDate,
            patientInitials: fields.patientInitials,
          });
          setStep('deliveries');
        },
      ]);
    },
    [
      fields.patientInitials,
      fields.receivedDate,
      fields.supplier,
      fields.surgeon,
      fields.surgeryDate,
      historyStack,
      matchingOrders,
      setFields,
    ],
  );

  const onSurgerySelectContinue = useCallback(
    async (orderRecordId?: string) => {
      // lookup the order and populate the fields
      const order = orderRecordId && matchingOrders.find(o => o.orderRecordId == orderRecordId);

      setOrderRecordId(orderRecordId);
      if (order) {
        setFields({
          ...fields,
          orderNumber: order.orderNumber,
          surgeryReference: order.surgeryReference,
          supplierRep: order.deviceRepId ? supplierRepOptions.find(rep => rep.id == order.deviceRepId) : undefined,
          patientInitials: fields.patientInitials || '',
        });
      } else {
        setField('createNewOrder', true);
      }

      setStep('save');

      setHistoryStack([
        ...historyStack,
        () => {
          setFields({
            ...fields,
            orderNumber: INITIAL_RECEIVE_DELIVERY_FIELDS.orderNumber,
            surgeryReference: INITIAL_RECEIVE_DELIVERY_FIELDS.surgeryReference,
            supplierRep: INITIAL_RECEIVE_DELIVERY_FIELDS.supplierRep,
            createNewOrder: INITIAL_RECEIVE_DELIVERY_FIELDS.createNewOrder,
          });
          setStep('surgeries');
        },
      ]);
    },
    [fields, historyStack, matchingOrders, setField, setFields, supplierRepOptions],
  );

  const onSave = useCallback(async () => {
    if (
      !fields.receivedDate ||
      !fields.supplier ||
      !fields.surgeon ||
      !fields.surgeryDate ||
      !fields.deliveryContents ||
      !(fields.surgeryReference || fields.orderNumber) ||
      !fields.patientInitials ||
      !fields.shipper ||
      !hasShipperDetails
    ) {
      console.warn('onSave: called with invalid fields', fields);

      throw new Error('Invalid fields');
    }

    // the surgery date has time attached and we only want the day
    const formattedSurgeryDateWithoutTime = replaceTimeInfoWithUtcInDate(fields.surgeryDate);

    const shipperDetailsExtras = { shipper: fields.shipper, shipmentId: fields.shipmentId };

    // the due date is the surgery date + 2 days
    const dueDate = new Date(formattedSurgeryDateWithoutTime);
    dueDate.setDate(dueDate.getDate() + 2);
    const formattedDueDate = dueDate.toISOString();

    // we need to store the created or dispatched delivery id so that we can show the check in delivery dialog later
    let deliveryIdForCheckIn = deliveryId;

    // if the supplier is connected but the delivery was not found
    let bypassedConnectedSupplier = false;

    // in one scenario the order number and delivery number may match an
    //  existing dispatched delivery, lookup the delivery if so
    let _isDispatchedDelivery = isDispatchedDelivery;
    let _deliveryId = deliveryId;
    let _delivery = delivery;
    if (!delivery && fields.orderNumber && fields.deliveryNumber) {
      const order = matchingOrders.find(o => o.orderNumber == fields.orderNumber);
      const delivery = order?.deliveries.find(d => d.deliveryNumber == fields.deliveryNumber);
      if (delivery) {
        _isDispatchedDelivery = delivery.deliveryStage == 'OUTBOUND';
        if (_isDispatchedDelivery) {
          _deliveryId = delivery.deliveryId;
          // useDelivery refetch doesnt allow to pass in the deliveryId
          _delivery = await API.deliveries.getDelivery(_deliveryId);
        }
      }
    }

    // if we're receiving an existing dispatched delivery we need to call the receive dispatch endpoint
    if (_isDispatchedDelivery && _deliveryId && _delivery) {
      await deliveryReceiveDispatch({
        deliveryId: _deliveryId,
        request: {
          status: 'ONGOING',
          stage: 'RECEIVE',
        },
      });

      // make a delivery update record if the delivery needs update
      const inDate = new Date(_delivery.receivedDetails?.receivedAt || ''); // intended to create an invalid date if not set
      const isChanged =
        !isSameDay(inDate, fields.receivedDate) ||
        (fields.deliveryContents && _delivery.content != fields.deliveryContents.id) ||
        fields.surgeryReference != _delivery.surgery.surgeryReference ||
        fields.orderNumber != _delivery.orderNumber ||
        fields.numRoadCases != _delivery.shipperDetails?.numRoadCases ||
        fields.numTubs != _delivery.shipperDetails?.numTubs ||
        fields.numBoxes != _delivery.shipperDetails?.numBoxes ||
        fields.numPelicanCases != _delivery.shipperDetails?.numPelicanCases ||
        fields.numDoubleRoadCases != _delivery.shipperDetails?.numDoubleRoadCases ||
        fields.patientInitials != _delivery.patientInitials;

      if (isChanged) {
        const updatedDelivery: UpdatedDelivery = {
          dueDate: formattedDueDate,
          receivedAt: fields.receivedDate.toISOString(),
          priority: _delivery.priority || 1,
          supplierTenantId: fields.supplier.id,
          deviceRepId: fields.supplierRep && '' + fields.supplierRep.id,
          surgeon: fields.surgeon.id,
          surgeryDate: formattedSurgeryDateWithoutTime,
          clinicalArea: _delivery.surgery.clinicalArea,
          surgeryReference: fields.surgeryReference || '',
          content: fields.deliveryContents?.id || 0,
          deliveryNumber: _delivery.deliveryNumber,
          orderNumber: fields.orderNumber || '',
          patientInitials: fields.patientInitials || '',
          shipperDetails: {
            numRoadCases: fields.numRoadCases || 0,
            numTubs: fields.numTubs || 0,
            numBoxes: fields.numBoxes || 0,
            numPelicanCases: fields.numPelicanCases || 0,
            numDoubleRoadCases: fields.numDoubleRoadCases || 0,
          },
          ...shipperDetailsExtras,
        };

        await updateDelivery({ deliveryId: _deliveryId, updatedDelivery });
      }
    }

    // otherwise we need to call the create endpoint
    else {
      let updatedDelivery: DeliveryBody;

      // if the receivedDate is not the current date, set the time to midday.
      const receivedDate = new Date(fields.receivedDate);
      if (!isSameDay(receivedDate, new Date())) {
        receivedDate.setHours(12, 0, 0, 0);
      }

      const deliveryBody = {
        receivedAt: receivedDate.toISOString(),
        supplierTenantId: fields.supplier.id,
        deviceRepId: fields.supplierRep && '' + fields.supplierRep.id,
        orderNumber: fields.orderNumber || '',
        deliveryNumber: fields.deliveryNumber || '',
        // this does not update the patient initials correctly it seems?
        surgeonId: fields.surgeon?.id,
        surgeryDate: formattedSurgeryDateWithoutTime,
        dueDate: formattedDueDate,
        content: fields.deliveryContents?.id || 0,
        surgeryReference: fields.surgeryReference || '',
        shipperDetails: {
          numRoadCases: fields.numRoadCases || 0,
          numTubs: fields.numTubs || 0,
          numBoxes: fields.numBoxes || 0,
          numPelicanCases: fields.numPelicanCases || 0,
          numDoubleRoadCases: fields.numDoubleRoadCases || 0,
        },
        ...shipperDetailsExtras,
        bypassedConnectedSupplier,
        createNewOrder: fields.createNewOrder,
        clinicalArea: '', // legacy field
      };

      const orderResponse = orderRecordId ? await fetchOrder(orderRecordId) : null;

      // if the value is not TBC and the surgery already exists and the patient initials used to be 'TBC', update it to the new, actual patient initials
      if (fields?.patientInitials !== 'TBC' && orderResponse?.surgery.patientInitials == 'TBC') {
        updatedDelivery = {
          ...deliveryBody,
          patientInitials: fields.patientInitials,
        };
        // if it is TBC and the surgery doesn't exist yet - send 'TBC' to the backend
      } else if (!orderResponse?.surgery.surgeryId) {
        updatedDelivery = {
          ...deliveryBody,
          patientInitials: fields.patientInitials,
        };
        // else, if the surgery exists and the patient initials given ARE 'TBC' - don't change anything - keep the current initials
        // this is for cases where the initials already exist and the user enters 'TBC' - we don't want to overwrite the existing initials
      } else {
        updatedDelivery = deliveryBody;
      }

      bypassedConnectedSupplier =
        !!isActiveSupplier &&
        !matchingOrders.some(
          o => o.orderNumber == fields.orderNumber && o.deliveries.some(d => d.deliveryNumber == fields.deliveryNumber),
        );

      try {
        const createdDelivery = await createDelivery(updatedDelivery);

        // if this delivery is for turnaround set(s) and/or implant(s), add them to the delivery
        // in a single request (the backend applies them atomically, avoiding the race condition
        // that per-set/per-call requests would have on the same delivery record)
        if (turnaroundSets?.length || turnaroundImplants?.length) {
          if (turnaroundImplants?.length && !turnaroundOriginDeliveryId) {
            throw new Error('Turnaround origin delivery ID is required');
          }

          await addAllTurnaroundsToDelivery({
            deliveryId: createdDelivery.deliveryId,
            request: {
              kitInstanceIds: turnaroundSets?.length ? turnaroundSets : undefined,
              originImplantDeliveryId: turnaroundImplants?.length ? Number(turnaroundOriginDeliveryId) : undefined,
            },
          });
        }

        deliveryIdForCheckIn = createdDelivery.deliveryId;
      } catch (error) {
        API.errorHandler(error);
        return;
      }
    }

    // fire and forget logging
    trackEvent(EventType.ReceiveDelivery, {
      status: _delivery?.status || 'RECEIVED',
      order_id: fields.orderNumber || '',
      received_by: _delivery?.receivedDetails?.receivedBy || '',
      current_user: user?.userId || '',
      tenant_name: user?.tenantName || '',
      bypassed_connected_supplier: bypassedConnectedSupplier,
    }).catch(() => {});

    toast.success(lang.success);

    // show the success dialog rather than blocking here on generating the received document;
    // the user can trigger that (and check in now) from the success dialog itself
    if (deliveryIdForCheckIn) {
      setReceiveSuccessDeliveryId(deliveryIdForCheckIn);
    }

    onComplete?.(deliveryIdForCheckIn, fields.deliveryNumber);

    setStep('done');
  }, [
    fields,
    hasShipperDetails,
    deliveryId,
    isDispatchedDelivery,
    delivery,
    user?.userId,
    user?.tenantName,
    lang.success,
    onComplete,
    matchingOrders,
    deliveryReceiveDispatch,
    updateDelivery,
    orderRecordId,
    fetchOrder,
    isActiveSupplier,
    createDelivery,
    turnaroundSets,
    turnaroundImplants?.length,
    addAllTurnaroundsToDelivery,
    turnaroundOriginDeliveryId,
  ]);

  const onReceiveDeliveryDialogClose = useCallback(() => {
    // if this is a new delivery and we have unsaved changes, show the unsaved changes dialog
    if (
      !deliveryId &&
      (fields.supplier || fields.surgeon || fields.surgeryDate || fields.deliveryContents || hasShipperDetails)
    ) {
      setShowUnsavedChangesDialog(true);
    } else if (onClose) {
      onClose();
    }
  }, [
    deliveryId,
    fields.deliveryContents,
    fields.supplier,
    fields.surgeon,
    fields.surgeryDate,
    hasShipperDetails,
    onClose,
  ]);

  const onReceiveSuccessDialogClose = useCallback(() => {
    setReceiveSuccessDeliveryId(null);
    onClose?.();
  }, [onClose]);

  const goBack = useMemo(() => {
    if (historyStack.length) {
      return () => {
        const historyStackCopy = [...historyStack];
        const handler = historyStackCopy.pop();
        setHistoryStack(historyStackCopy);
        handler?.();
      };
    }
  }, [historyStack]);

  // reset the fields on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => resetFields, []);

  return isLoadingInitial ? (
    <Dialog open title={lang.title} sx={dialogWidthSx}>
      <PageSpinner />
    </Dialog>
  ) : (
    <>
      <Dialog
        {...props}
        open
        title={lang.title}
        heading={<div style={{ maxWidth: '628px' }}>{lang.headings[step]}</div>}
        onClose={onReceiveDeliveryDialogClose}
        onBack={goBack}
        sx={dialogWidthSx}
      >
        {step == 'search' ? (
          <OrdersSearch
            delivery={delivery}
            surgeonOptions={surgeonOptions}
            supplierOptions={supplierOptions}
            haveSupplier={!!haveDefaultSupplier}
            isActiveSupplier={isActiveSupplier}
            onContinue={onOrdersSearchContinue}
          />
        ) : step == 'deliveries' ? (
          <DeliverySelect
            orders={matchingOrders}
            supplierOptions={supplierOptions}
            onContinue={onDeliverySelectContinue}
            isLoading={deliveryLoading}
            selectedSupplier={selectedSupplier}
          />
        ) : step == 'surgeries' ? (
          <SurgerySelect
            orders={matchingOrders}
            supplierOptions={supplierOptions}
            onContinue={onSurgerySelectContinue}
          />
        ) : step == 'save' ? (
          <DeliverySave
            delivery={delivery}
            matchingOrders={matchingOrders}
            supplierRepOptions={supplierRepOptions}
            onContinue={onSave}
            isSaving={isSaving}
          />
        ) : null}
      </Dialog>

      {showUnsavedChangesDialog && (
        <Dialog
          open
          title={lang.unsavedChangesDialog.title}
          heading={lang.unsavedChangesDialog.heading}
          confirmLabel={lang.unsavedChangesDialog.confirm}
          cancelLabel={lang.unsavedChangesDialog.cancel}
          onConfirm={onClose}
          onClose={() => setShowUnsavedChangesDialog(false)}
          onCancel={() => setShowUnsavedChangesDialog(false)}
        />
      )}

      {receiveSuccessDeliveryId && (
        <ReceiveDeliverySuccessDialog
          deliveryId={receiveSuccessDeliveryId.toString()}
          onClose={onReceiveSuccessDialogClose}
        />
      )}
    </>
  );
};
